import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { IAppState } from '~/appstate';
import { ApiRequestError, ApiServerError, unwrapFetch2 } from '~/fetch';
import type { GetGlGroupsResult, GlGroup } from '~/ogm-hub';
import { GeneralLedgerController } from '~/ogm-hub';
import { LoadingStatus } from './schema';

export interface IGlGroupsState extends EntityState<GlGroup> {
	allFetched: boolean;
	status: LoadingStatus;
}

export const fetchGlGroups = createAsyncThunk<
	GlGroup[],
	void,
	{ rejectValue: string[] | Response }
>('glGroups/fetchGlGroups', async (_, { rejectWithValue }) => {
	try {
		const result = (await unwrapFetch2(
			GeneralLedgerController.GetGlGroups()
		)) as GetGlGroupsResult;
		return result.glGroups;
	} catch (err) {
		if (err instanceof ApiRequestError) {
			return rejectWithValue(err.errors);
		}
		if (err instanceof ApiServerError) {
			return rejectWithValue(err.response);
		}
		throw err;
	}
});

export const glGroupsAdapter = createEntityAdapter<GlGroup>({
	selectId: (glGroup) => glGroup.groupId,
});

const initialState: IGlGroupsState = glGroupsAdapter.getInitialState({
	allFetched: false,
	status: LoadingStatus.idle,
});

export const glGroupsSlice = createSlice({
	name: 'glGroups',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchGlGroups.pending, (state) => {
			state.status = LoadingStatus.loading;
		});
		builder.addCase(fetchGlGroups.rejected, (state) => {
			state.status = LoadingStatus.failure;
		});
		builder.addCase(fetchGlGroups.fulfilled, (state, action) => {
			glGroupsAdapter.setAll(state, action.payload);
			state.allFetched = true;
			state.status = LoadingStatus.success;
		});
	},
});

export function selectGlGroups(state: IAppState) {
	return Object.values(state.glGroups.entities);
}

export function selectGlGroupById(state: IAppState, id: number) {
	return state.glGroups.entities[id];
}

export function selectGlGroupLoadingStatus(state: IAppState) {
	return state.glGroups.status;
}
